<template>
    <section class="how-to-use-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
            </div>

            <div class="steps-container">
                <div
                    v-for="(step, index) in steps"
                    :key="index"
                    class="step-item"
                >
                    <div class="step-number">{{ index + 1 }}</div>
                    <div class="step-content">
                        <h3 class="step-title">{{ step.title }}</h3>
                        <p class="step-description">{{ step.description }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
interface Step {
    title: string
    description: string
}

interface Props {
    title: string
    steps: Step[]
}

defineProps<Props>()
</script>

<style lang="less" scoped>
.how-to-use-section {
    padding: 6rem 0;
    background: #120e11;
    padding-top: 3.75rem;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

.steps-container {
    display: flex;
    justify-content: space-between;
    position: relative;
    gap: 2rem;
    max-width: 1400px;
    margin: 0 auto;
    padding: 5rem;
    border-radius: 8px;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url('https://static.weryai.com/growth/image/28cd14ee5659acb6b48ff315a8709737.webp');
        background-size: cover;
        background-position: center;
        z-index: 1;
    }

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            to bottom,
            #120e11 0%,
            #120e11 10%,
            rgba(18, 14, 17, 0.7) 30%,
            rgba(18, 14, 17, 0.7) 50%,
            rgba(18, 14, 17, 0.3) 100%
        );
        z-index: 2;
    }
}

.step-item {
    flex: 1;
    text-align: left;
    backdrop-filter: blur(10px);
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 1rem 2rem;
    position: relative;
    z-index: 4;
}

.step-number {
    width: 3rem;
    height: 3rem;
    position: relative;
    background-color: #252527;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    margin: 0 auto 0.75rem;
    margin-left: 5px;

    &::before {
        content: '';
        position: absolute;
        top: -5px;
        left: -5px;
        right: -5px;
        bottom: -5px;
        border-radius: 50%;
        box-shadow: 4px 4px 6px rgba(255, 255, 255, 0.4) inset;
        z-index: -1;
        border-top: 1px solid rgba(255, 255, 255, 0.8);
    }
}

.step-content {
    text-align: left;
}

.step-title {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: white;
}

.step-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    margin: 0;
}

@media (max-width: 768px) {
    .how-to-use-section {
        padding: 3rem 0;
    }

    .section-title {
        font-size: 2rem;
        padding: 0 1rem;
    }

    .section-header {
        margin-bottom: 2rem;
        padding: 0 1rem;
    }

    .steps-container {
        flex-direction: column;
        gap: 1.5rem;
        padding: 3rem 1.5rem;
    }

    .step-item {
        padding: 1rem 1.5rem;
    }

    .step-number {
        width: 2.5rem;
        height: 2.5rem;
        font-size: 1.25rem;
    }

    .step-title {
        font-size: 0.9rem;
    }

    .step-description {
        font-size: 0.8rem;
    }
}

@media (max-width: 480px) {
    .how-to-use-section {
        padding: 2rem 0;
    }

    .section-title {
        font-size: 1.5rem;
    }

    .section-header {
        margin-bottom: 1.5rem;
    }

    .steps-container {
        padding: 2rem 1rem;
        gap: 1rem;
    }

    .step-item {
        padding: 0.875rem 1rem;
    }

    .step-number {
        width: 2rem;
        height: 2rem;
        font-size: 1rem;

        &::before {
            top: -3px;
            left: -3px;
            right: -3px;
            bottom: -3px;
        }
    }

    .step-title {
        font-size: 0.85rem;
    }

    .step-description {
        font-size: 0.75rem;
    }
}
</style>

